import { Box, IconButton, type SxProps, type Theme, useTheme } from "@mui/material";
import { type ReactNode, memo } from "react";

import { CloseIcon } from "components/icons";

type ChipV2Variant = "info" | "warning" | "success" | "neutral";

interface ChipV2Props {
  variant: ChipV2Variant;
  children: ReactNode;
  containerSx?: SxProps<Theme>;
  clearable?: boolean;
  onClear?: () => void;
}

export const ChipV2 = memo(
  ({ variant, children, containerSx, clearable = false, onClear }: ChipV2Props) => {
    const theme = useTheme();

    const variantStyles: Record<
      ChipV2Variant,
      { backgroundColor: string | undefined; color: string | undefined }
    > = {
      info: {
        backgroundColor: theme.palette.primitive.system.blue[50],
        color: theme.palette.lightBg.high,
      },
      warning: {
        backgroundColor: theme.palette.primitive.system.orange[50],
        color: theme.palette.lightBg.high,
      },
      success: {
        backgroundColor: theme.palette.primitive.system.green[50],
        color: theme.palette.lightBg.high,
      },
      neutral: {
        backgroundColor: theme.palette.primitive.neutrals.neutral[100],
        color: theme.palette.lightBg.high,
      },
    };

    const { backgroundColor, color } = variantStyles[variant];

    return (
      <Box
        sx={{
          height: "24px",
          padding: "4px 8px",
          borderRadius: "4px",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          backgroundColor,
          color,
          ...theme.typography["caption-xs"],
          ...(containerSx as Record<string, unknown>),
        }}
      >
        {children}
        {clearable && (
          <IconButton
            size="small"
            onClick={onClear}
            aria-label="Remove"
            sx={{
              padding: 0,
              color: "inherit",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            <CloseIcon sx={{ fontSize: "14px" }} />
          </IconButton>
        )}
      </Box>
    );
  },
);

ChipV2.displayName = "ChipV2";
